import React from 'react'
import { Box, Typography } from '@mui/material'
import { useLocation } from 'react-router'

// Marks the URL we most recently handed to the server, so a path that comes back
// to us unrouted a second time is recognised as owned by nobody instead of being
// bounced again. sessionStorage (not a module variable) because the bounce
// replaces the document, which resets module state.
const BOUNCED_KEY = 'hardNavigate:bouncedTo'

/**
 * Catch-all route element: hands a path back to the server so Next.js can own it.
 *
 * This app and the Next.js app share an origin, so React Router will happily match
 * a path client-side that Next.js is supposed to own — the URL changes, no request
 * is made, and the visitor silently stays on the legacy PWA. Because that navigation
 * never leaves the browser, nothing server-side (proxy.ts, rewrites) can intercept it.
 *
 * Registering this as the catch-all inverts the default: this app claims only the
 * paths it explicitly routes, and anything else is forced through the server, where
 * the proxy decides. Migrating a page therefore means deleting its <Route> here —
 * no allow-list to maintain and nothing to remember to un-claim.
 *
 * Loop guard: we record the URL before handing it over. If the server sends that same
 * URL straight back and we still match nothing, no app owns it, so we stop and render
 * a visible message rather than bouncing forever OR rendering a blank page. That blank
 * is the trap worth naming: deleting a <Route> here while proxy.ts still rewrites the
 * path to this app would otherwise strand visitors on an empty screen with no error.
 *
 * `location.replace` (never `.href`) is load-bearing: it keeps the bounced URL out of
 * history, so Back returns to wherever the visitor actually came from. With `.href`,
 * Back would land on the entry we just bounced away from and immediately bounce again.
 */
export default function HardNavigate() {
  const { pathname, search, hash } = useLocation()
  // Compare the full URL, not just the path: a query- or hash-only change is still a
  // different address that the server may route differently.
  const url = `${pathname}${search}${hash}`
  const [unowned, setUnowned] = React.useState(false)

  React.useEffect(() => {
    if (window.sessionStorage.getItem(BOUNCED_KEY) === url) {
      window.sessionStorage.removeItem(BOUNCED_KEY)
      setUnowned(true)
      return
    }
    window.sessionStorage.setItem(BOUNCED_KEY, url)
    // Note: only the URL survives the handover. A route reached via
    // navigate(path, { state }) loses that state — pass anything durable in the URL.
    window.location.replace(url)
  }, [url])

  if (!unowned) return null

  return (
    <Box sx={{ py: 10, textAlign: 'center' }}>
      <Typography variant='h6'>This page isn&apos;t available.</Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
        The address {url} didn&apos;t match anything.
      </Typography>
    </Box>
  )
}
